import { useQueries } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import { useEntityAssociationsList } from '@/api/rpc/hooks/core/useEntityAssociations';
import { EntityAssociationType, SystemEntity } from '@erp/shared-types';
import type { Charge, PurchaseOrderLineItem } from '@erp/rpc';
import { useLineItemAssociationsMap } from './useLineItemAssociationsMap';
import {
  buildMatchedLineNumbers,
  buildProposedLineNumbers,
  buildRelatedLinesSummary,
  selectRelatedDocuments,
} from './relatedLinesSummary';

// Module-level so `useQueries` keeps the combined result stable across renders.
function combineCharges(
  results: { data?: { charges: Charge[] }; isLoading: boolean }[],
) {
  return {
    charges: results.map((result) => result.data?.charges ?? []),
    isLoading: results.some((result) => result.isLoading),
  };
}

function combinePOLineItems(
  results: {
    data?: {
      lineItems: PurchaseOrderLineItem[];
      purchaseOrder?: { supplierId?: string | null } | null;
    };
    isLoading: boolean;
  }[],
) {
  return {
    lineItems: results.map((result) => result.data?.lineItems ?? []),
    supplierIds: results.map(
      (result) => result.data?.purchaseOrder?.supplierId ?? null,
    ),
    isLoading: results.some((result) => result.isLoading),
  };
}

interface UseRelatedLinesSummaryInput {
  invoiceId: string;
  lineItems: { id: string; lineNumber: number }[];
  invoiceTotal: string | null;
  invoiceSupplierId?: string | null;
  /** Also load Famous reversal and superseded rows (history only). */
  showHistory: boolean;
}

/**
 * Loads the documents an AP invoice is matched against and folds their
 * charges and PO lines into the "Related lines" summary.
 */
export function useRelatedLinesSummary({
  invoiceId,
  lineItems,
  invoiceTotal,
  invoiceSupplierId,
  showHistory,
}: UseRelatedLinesSummaryInput) {
  const invoiceAssociationsQuery = useEntityAssociationsList({
    entityId: invoiceId,
    entityType: SystemEntity.AP_INVOICE,
    associationType: EntityAssociationType.AP_INVOICE_MATCHING,
  });
  const documents = selectRelatedDocuments(
    invoiceAssociationsQuery.data?.associations ?? [],
  );

  const chargesByDocument = useQueries({
    queries: documents.map((document) =>
      orpc.finance.charges.getForEntity.queryOptions({
        input: { entity: document, includeLineCharges: false },
      }),
    ),
    combine: combineCharges,
  });

  // Only fetched once someone asks for the history: Famous reversal and
  // superseded rows, which never count toward totals.
  const allChargesByDocument = useQueries({
    queries: documents.map((document) =>
      orpc.finance.charges.getForEntity.queryOptions({
        input: {
          entity: document,
          includeLineCharges: false,
          includeInactive: true,
        },
        enabled: showHistory,
      }),
    ),
    combine: combineCharges,
  });

  const poLineItemsByDocument = useQueries({
    queries: documents.map((document) =>
      orpc.accountsPayable.purchaseOrders.get.queryOptions({
        input: { id: document.entityId },
        enabled: document.entityType === SystemEntity.INTERNAL_PURCHASE_ORDER,
      }),
    ),
    combine: combinePOLineItems,
  });

  const lineAssociations = useLineItemAssociationsMap(lineItems);

  const summary = buildRelatedLinesSummary({
    documents: documents.map((document, index) => ({
      ...document,
      charges: chargesByDocument.charges[index] ?? [],
      inactiveCharges: showHistory
        ? allChargesByDocument.charges[index]
        : undefined,
      lineItems: poLineItemsByDocument.lineItems[index] ?? [],
      supplierId: poLineItemsByDocument.supplierIds[index],
    })),
    matches: buildMatchedLineNumbers(lineItems, lineAssociations),
    proposed: buildProposedLineNumbers(lineItems, lineAssociations),
    invoiceTotal,
    invoiceSupplierId,
  });

  const isLoading =
    invoiceAssociationsQuery.isLoading ||
    chargesByDocument.isLoading ||
    (showHistory && allChargesByDocument.isLoading) ||
    poLineItemsByDocument.isLoading;

  return { summary, hasDocuments: documents.length > 0, isLoading };
}
